/**
 * Flat list of every DCS node that participates in the navigation
 * tree — DcsController, Cee, ControlModule, FunctionBlock, IoModule,
 * IoChannel — with parent ids so {@link DcsTreeNav} can rebuild the
 * hierarchy client-side (same pattern as `PLANT_PLC_TREE_CYPHER`).
 *
 * ControlTags / alarms / setpoints are not tree rows; their ontology
 * total rides `totalControlTagCount` for the tab subtitle.
 *
 * Sequence (`step` / `transition` / `handler`) and comms/device FBs stay
 * off the rail — they live on ControlModule / Cee / DcsController detail.
 * Tree FunctionBlocks are only logic children of a ControlModule (`block_role`
 * plus STEP/TRANSITION/HANDLER `block_type`, so a missing role still stays off).
 *
 * Parent edges:
 *   Cee            <-[:HOSTS]- DcsController
 *   ControlModule  -[:ASSIGNED_TO]-> Cee
 *   FunctionBlock  <-[:CONTAINS]- ControlModule
 *   IoModule       -[:ASSIGNED_TO|MOUNTED_ON]-> DcsController
 *     (Experion ingest writes both edges to the same controller;
 *     parent resolution uses `head()` so the rail does not duplicate)
 *   IoChannel      <-[:CONTAINS]- IoModule
 */
import { dcsLogicFbPredicate } from './dcs-predicates'

export const PLANT_DCS_TREE_CYPHER = `
            CALL {
              MATCH (t:ControlTag)
              RETURN count(t) AS totalControlTagCount
            }
            MATCH (n)
              WHERE (n:DcsController OR n:Cee OR n:ControlModule
                     OR n:IoModule OR n:IoChannel
                     OR (n:FunctionBlock
                         AND ${dcsLogicFbPredicate('n')}
                         AND (n)<-[:CONTAINS]-(:ControlModule)))
            WITH n, totalControlTagCount,
                 CASE
                   WHEN n:Cee THEN head([(n)<-[:HOSTS]-(p:DcsController) | p])
                   WHEN n:ControlModule THEN head([(n)-[:ASSIGNED_TO]->(p:Cee) | p])
                   WHEN n:FunctionBlock THEN head([(n)<-[:CONTAINS]-(p:ControlModule) | p])
                   WHEN n:IoModule THEN head([(n)-[:ASSIGNED_TO|MOUNTED_ON]->(p:DcsController) | p])
                   WHEN n:IoChannel THEN head([(n)<-[:CONTAINS]-(p:IoModule) | p])
                   ELSE null
                 END AS parent
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HOSTS]->(c:Cee)
                WHERE n:DcsController
              WITH n, count(distinct c) AS ceeCount
              OPTIONAL MATCH (iom:IoModule)-[:ASSIGNED_TO|MOUNTED_ON]->(n)
                WHERE n:DcsController
              WITH n, ceeCount, count(distinct iom) AS ioModuleCount
              OPTIONAL MATCH (n)<-[:ASSIGNED_TO]-(cm:ControlModule)
                WHERE n:Cee
              WITH n, ceeCount, ioModuleCount, count(distinct cm) AS moduleCount
              OPTIONAL MATCH (n)-[:CONTAINS]->(fb:FunctionBlock)
                WHERE n:ControlModule AND ${dcsLogicFbPredicate('fb')}
              WITH n, ceeCount, ioModuleCount, moduleCount, count(distinct fb) AS blockCount
              OPTIONAL MATCH (n)-[:CONTAINS]->(ch:IoChannel)
                WHERE n:IoModule
              WITH n, ceeCount, ioModuleCount, moduleCount, blockCount,
                   count(distinct ch) AS channelCount
              RETURN CASE
                       WHEN n:DcsController THEN ceeCount + ioModuleCount
                       WHEN n:Cee THEN moduleCount
                       WHEN n:ControlModule THEN blockCount
                       WHEN n:IoModule THEN channelCount
                       ELSE 0
                     END AS childCount
            }
            RETURN totalControlTagCount,
                   n AS node,
                   CASE WHEN parent IS NULL THEN NULL ELSE parent.id END AS parentId,
                   CASE
                     WHEN n:DcsController THEN 'DcsController'
                     WHEN n:Cee THEN 'Cee'
                     WHEN n:ControlModule THEN 'ControlModule'
                     WHEN n:FunctionBlock THEN 'FunctionBlock'
                     WHEN n:IoModule THEN 'IoModule'
                     WHEN n:IoChannel THEN 'IoChannel'
                   END AS kind,
                   childCount
            ORDER BY toLower(coalesce(n.name, n.id))
            LIMIT 10000
          `
